import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { hideLoading, showLoading } from '../../actions/api';
import { showNotification } from '../../actions/notifications';
import { StorageKind, StorageItem } from '../../constants/document';
import NotificationsTypes from '../../constants/notification';
import documentsService from '../../services/documentsService';
import { annotateDocumentCounts, forceDownload } from '../../utils';
import Confirmation from '../dialog/confirmation/Confirmation';
import DocumentsViewer from '../documents-viewer/DocumentsViewer';
import TreeSkeleton from '../loading/TreeSkeleton';
import TreeViewDocuments from '../tree-view/TreeViewDocuments';
import { IMessageConfirmation } from '../users/@types';

const initialModalState: IMessageConfirmation = {
  message: '',
  callback: () => undefined,
};

const TREE_PANE_SIZE_STORAGE_KEY = 'docs-share/tree-pane-size';
const TREE_EXPAND_STORAGE_KEY = 'docs-share/tree-expand-ids';
const TREE_SELECTED_PATH_STORAGE_KEY = 'docs-share/tree-selected-path';
const DEFAULT_TREE_PANE_SIZE = '35%';

const readStoredSelectedPath = (): string | null => {
  try {
    return localStorage.getItem(TREE_SELECTED_PATH_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Walks the storage tree depth-first looking for an item whose `path`
 * matches `targetPath`. Used to validate / restore the persisted tree
 * selection across sessions and across mutation refreshes.
 */
const findItemByPath = (
  items: StorageItem[] | undefined,
  targetPath: string,
): StorageItem | null => {
  if (!items) return null;
  for (const item of items) {
    if (item.path === targetPath) return item;
    const inner = findItemByPath(item.items as StorageItem[] | undefined, targetPath);
    if (inner) return inner;
  }
  return null;
};

const readStoredTreePaneSize = (): string => {
  try {
    return localStorage.getItem(TREE_PANE_SIZE_STORAGE_KEY) ?? DEFAULT_TREE_PANE_SIZE;
  } catch {
    return DEFAULT_TREE_PANE_SIZE;
  }
};

const DocumentsManager = () => {
  const dispatch = useDispatch();
  const [blobs, setBlobs] = useState<StorageItem[]>([]);
  const [fileSelected, setFileSelected] = useState<any>({});
  const [showConfirmationMessage, setShowConfirmationMessage] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<IMessageConfirmation>(initialModalState);
  const [hasLoadedTree, setHasLoadedTree] = useState<boolean>(false);
  const [panes, setPanes] = useState<SplitterPaneProps[]>(() => [
    {
      size: readStoredTreePaneSize(),
      min: '220px',
    },
    {},
  ]);

  const splitterContainerRef = useRef<HTMLDivElement>(null);
  const treePaneRef = useRef<HTMLDivElement>(null);

  const onSplitterChange = useCallback((e: SplitterOnChangeEvent) => {
    setPanes(e.newState);
    if (e.isLast && e.newState[0]?.size) {
      try {
        localStorage.setItem(TREE_PANE_SIZE_STORAGE_KEY, e.newState[0].size);
      } catch {
        /* ignore quota / privacy-mode errors */
      }
    }
  }, []);

  // Double-click on the splitter bar fits the tree pane to the natural width
  // of the longest visible folder/file row. Measures each rendered
  // `.tree-item-label` span (added by TreeViewItem) which is inline and
  // therefore keeps its content-width regardless of the current pane size,
  // and uses the rightmost edge as the natural width of the longest item.
  // Without this approach, `treeView.scrollWidth` would just echo the
  // current pane width whenever the content already fits — meaning each
  // double-click would grow the pane by the padding constant indefinitely.
  // Clamped to [220px, 70% of container].
  const fitTreePaneToContent = useCallback(() => {
    const wrapper = treePaneRef.current;
    if (!wrapper) return;
    const treeView = wrapper.querySelector<HTMLElement>('.k-treeview');
    if (!treeView) return;
    const labels = treeView.querySelectorAll<HTMLElement>('.tree-item-label');
    if (labels.length === 0) return;
    const treeLeft = treeView.getBoundingClientRect().left;
    let maxRight = 0;
    labels.forEach((el) => {
      const right = el.getBoundingClientRect().right;
      if (right > maxRight) maxRight = right;
    });
    const naturalWidth = maxRight - treeLeft;
    if (naturalWidth <= 0) return;
    const containerWidth = splitterContainerRef.current?.offsetWidth ?? window.innerWidth;
    const maxWidth = Math.floor(containerWidth * 0.7);
    const padding = 32; // breathing room for icon-text gap and scrollbar gutter
    const finalWidth = Math.min(Math.max(Math.ceil(naturalWidth) + padding, 220), maxWidth);
    const newSize = `${finalWidth}px`;
    setPanes((prev) => {
      const next = [...prev];
      next[0] = { ...next[0], size: newSize };
      return next;
    });
    try {
      localStorage.setItem(TREE_PANE_SIZE_STORAGE_KEY, newSize);
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, []);

  useEffect(() => {
    const root = splitterContainerRef.current;
    if (!root) return;
    const splitbar = root.querySelector('.k-splitbar');
    if (!splitbar) return;
    const handler = (e: Event) => {
      // Run before Kendo's internal handlers (capture phase) and prevent
      // them from firing, otherwise the bar's own collapse / focus logic
      // can override the size we just computed.
      e.stopImmediatePropagation();
      e.preventDefault();
      fitTreePaneToContent();
    };
    splitbar.addEventListener('dblclick', handler, true);
    return () => splitbar.removeEventListener('dblclick', handler, true);
  }, [fitTreePaneToContent]);

  useEffect(() => {
    // Initial mount: skip the global spinner because the TreeSkeleton in the
    // tree pane already provides contextual loading feedback. Subsequent
    // refreshes (after rename / delete / upload mutations) go through the
    // same getData with `silent=false` and DO show the spinner.
    getData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) dispatch(showLoading());
        const response = await documentsService.getDocumentsList();
        if (response) {
          setBlobs(annotateDocumentCounts(response));
        }
      } catch (error: any) {
        setBlobs([]);
        dispatch(
          showNotification({
            message: 'Retrieve documents failed, try again later or contact the support team',
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        if (!silent) dispatch(hideLoading());
        setHasLoadedTree(true);
      }
    },
    [dispatch],
  );

  // ----- Persisted tree selection -------------------------------------------
  // Whenever `blobs` changes (initial load OR refresh after a mutation),
  // make sure `fileSelected` still points to a real item in the tree:
  //   1. If the current selection still exists, leave it alone.
  //   2. Otherwise try to restore the path stored in localStorage.
  //   3. Otherwise fall back to the first item in the tree (the root folder).
  // Uses the functional setter so we read the latest fileSelected without
  // adding it to the deps array (which would cause infinite loops).
  useEffect(() => {
    if (blobs.length === 0) return;
    setFileSelected((current: any) => {
      if (current?.path && findItemByPath(blobs, current.path)) {
        return current;
      }
      const savedPath = readStoredSelectedPath();
      if (savedPath) {
        const found = findItemByPath(blobs, savedPath);
        if (found) return found;
      }
      return blobs[0] ?? current;
    });
  }, [blobs]);

  // Mirror the current selection to localStorage on every change. Skipped
  // when the selection is the empty initial state so we don't write garbage
  // before the first real selection arrives.
  useEffect(() => {
    if (!fileSelected?.path) return;
    try {
      localStorage.setItem(TREE_SELECTED_PATH_STORAGE_KEY, fileSelected.path);
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [fileSelected?.path]);
  // --------------------------------------------------------------------------

  // Available to every role (no edit-rights gate). Triggered from the tree
  // context menu's "Download" entry on documents.
  const downloadDocument = useCallback(
    async (item: StorageItem) => {
      try {
        dispatch(showLoading());
        let url = (item as any).url as string | undefined;
        if (!url) {
          const full = await documentsService.getDocumentInfo(item.path);
          url = full?.url;
        }
        if (!url) throw new Error('No URL returned for document');
        await forceDownload(url, item.text);
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('[downloadDocument] failed:', error);
        dispatch(
          showNotification({
            message: `Download failed: ${error?.message ?? 'unknown error'}`,
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        dispatch(hideLoading());
      }
    },
    [dispatch],
  );

  // Available to every role (no edit-rights gate). Triggered from the tree
  // context menu's "Download as ZIP" entry on folders.
  const downloadFolder = useCallback(
    async (item: StorageItem) => {
      const folderName = item.text;
      try {
        dispatch(showLoading());
        const blob = await documentsService.downloadFolder(item.path);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${folderName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
        dispatch(
          showNotification({
            message: `Folder "${folderName}" downloaded successfully`,
            type: NotificationsTypes.Success,
          }),
        );
      } catch (error: any) {
        let message = 'Download failed, try again later';
        try {
          if (error?.data instanceof Blob) {
            const text = await error.data.text();
            const parsed = JSON.parse(text);
            if (parsed.error) message = parsed.error;
          }
        } catch {
          // Blob parsing failed — use status-based fallback
          if (error?.status === 404) {
            message = 'The folder is empty or does not exist';
          }
        }
        dispatch(showNotification({ message, type: NotificationsTypes.Error }));
      } finally {
        dispatch(hideLoading());
      }
    },
    [dispatch],
  );

  const deleteStorageDocument = useCallback(
    async (item: StorageItem): Promise<Boolean> => {
      var succeed = false;
      try {
        dispatch(showLoading());
        await documentsService.deleteDocument(item.text, item.path.substring(0, item.path.length - item.text.length));
        setFileSelected(null);
        succeed = true;
      } catch (error: any) {
        dispatch(
          showNotification({
            message: 'Delete document failed, try again later or contact the support team',
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        dispatch(hideLoading());
      }
      return succeed;
    },
    [dispatch],
  );

  const deleteStorageContentFolder = useCallback(
    async (path: string): Promise<Boolean> => {
      var succeed = false;
      try {
        dispatch(showLoading());
        await documentsService.deleteContentFolder(path);
        setFileSelected(null);
        succeed = true;
      } catch (error: any) {
        dispatch(
          showNotification({
            message: 'Delete content failed, try again later or contact the support team',
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        dispatch(hideLoading());
      }
      return succeed;
    },
    [dispatch],
  );

  const deleteStorageFolder = useCallback(
    async (path: string): Promise<Boolean> => {
      var succeed = false;
      try {
        dispatch(showLoading());
        await documentsService.deleteFolder(path);
        setFileSelected(null);
        succeed = true;
      } catch (error: any) {
        dispatch(
          showNotification({
            message: 'Delete folder failed, try again later or contact the support team',
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        dispatch(hideLoading());
      }
      return succeed;
    },
    [dispatch],
  );

  const getDocumentsViewer = () => {
    if (fileSelected != null && fileSelected.kind === StorageKind.document) {
      return <DocumentsViewer {...fileSelected} />;
    } else {
      return (
        <p style={{ fontSize: '14px', marginTop: '10px', textAlign: 'center' }}>
          Navigate through the tree and click to preview the file.
        </p>
      );
    }
  };

  const insertItemIntoTree = (path: string, blob: StorageItem, item: StorageItem) => {
    if (blob.path === path) {
      if (item.kind === StorageKind.document) blob.items?.push({ ...item, path: item.path + item.text });
      else blob.items?.push(item);

      blob.items = blob.items?.sort((a, b) => {
        if (a.text < b.text) {
          return -1;
        }
        if (a.text > b.text) {
          return 1;
        }
        return 0;
      });
    } else if (blob.items != null) {
      for (let i = 0; i < blob.items.length; i++) {
        insertItemIntoTree(path, blob.items[i], item);
      }
    }
  };

  const deleteItemIntoTree = useCallback((blob: StorageItem, item: StorageItem) => {
    if (blob.items != null) {
      var filtereddItems = blob.items.filter((i) => i.path === item.path);
      if (filtereddItems.length > 0) {
        blob.items = blob.items.filter((i) => i.path !== item.path);
      } else {
        for (let i = 0; i < blob.items.length; i++) {
          deleteItemIntoTree(blob.items[i], item);
        }
      }
    }
  }, []);

  const addFolder = async (path: string, folderName: string) => {
    if (await addStorageFolder(path, folderName)) {
      var item: StorageItem = {
        path: path + folderName + '/',
        text: folderName,
        kind: StorageKind.folder,
        url: undefined,
        contentType: undefined,
        items: [],
      };

      var copyBlobs = [...blobs];
      insertItemIntoTree(path, copyBlobs[0], item);
      setBlobs(copyBlobs);
    }
  };

  const addDocument = (path: string, items: StorageItem[]) => {
    var copyBlobs = [...blobs];

    for (const item of items) {
      insertItemIntoTree(path, copyBlobs[0], item);
    }

    setBlobs(copyBlobs);
  };

  const deleteDocument = useCallback(
    async (item: StorageItem) => {
      if (await deleteStorageDocument(item)) {
        var copyBlobs = [...blobs];
        deleteItemIntoTree(copyBlobs[0], item);
        setBlobs(copyBlobs);
      }
    },
    [blobs, deleteItemIntoTree, deleteStorageDocument],
  );

  const deleteFolder = useCallback(
    async (item: StorageItem) => {
      if (await deleteStorageFolder(item?.path)) {
        var copyBlobs = [...blobs];
        deleteItemIntoTree(copyBlobs[0], item);
        setBlobs(copyBlobs);
      }
    },
    [blobs, deleteItemIntoTree, deleteStorageFolder],
  );

  const deleteContentFolder = useCallback(
    async (item: StorageItem) => {
      if (await deleteStorageContentFolder(item?.path)) {
        var copyBlobs = [...blobs];
        item.items?.forEach((element) => {
          deleteItemIntoTree(copyBlobs[0], element);
        });
        setBlobs(copyBlobs);
      }
    },
    [blobs, deleteItemIntoTree, deleteStorageContentFolder],
  );

  const renameDocument = useCallback(
    async (item: StorageItem, newFileName: string) => {
      try {
        dispatch(showLoading());
        await documentsService.renameDocument(
          item.text,
          newFileName,
          item.path.substring(0, item.path.length - item.text.length),
        );
        await getData();
        setFileSelected(null);
      } catch (error: any) {
        dispatch(
          showNotification({
            message: 'Rename document failed, try again later or contact the support team',
            type: NotificationsTypes.Error,
          }),
        );
      } finally {
        dispatch(hideLoading());
      }
    },
    [dispatch, getData],
  );

  const renameFolder = async (item: StorageItem, newFolderName: string) => {
    try {
      dispatch(showLoading());
      await documentsService.renameFolder(
        item.text,
        newFolderName,
        item.path.endsWith('/')
          ? item.path.substring(0, item.path.length - item.text.length - 1)
          : item.path.substring(0, item.path.length - item.text.length),
      );
      await getData();
      setFileSelected(null);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: 'Rename folder failed, try again later or contact the support team',
          type: NotificationsTypes.Error,
        }),
      );
    } finally {
      dispatch(hideLoading());
    }
  };

  const addStorageFolder = async (path: string, folderName: string): Promise<Boolean> => {
    var succeed = false;
    try {
      dispatch(showLoading());
      await documentsService.addFolder(folderName, path);
      setFileSelected(null);
      succeed = true;
    } catch (error: any) {
      dispatch(
        showNotification({
          message: 'Create folder failed, try again later or contact the support team',
          type: NotificationsTypes.Error,
        }),
      );
    } finally {
      dispatch(hideLoading());
    }
    return succeed;
  };

  const onRenameDocument = useCallback(
    (item: StorageItem, newFileName: string) => {
      setConfirmationModal({
        title: `Rename file — ${item.text}`,
        message: `Are you sure you want to rename this file to "${newFileName}"?`,
        callback: () => renameDocument(item, newFileName),
      });
      setShowConfirmationMessage(true);
    },
    [renameDocument],
  );

  const onDeleteDocument = useCallback(
    (item: StorageItem) => {
      setConfirmationModal({
        title: `Delete file — ${item.text}`,
        message: `Are you sure you want to delete this file?`,
        callback: () => deleteDocument(item),
      });
      setShowConfirmationMessage(true);
    },
    [deleteDocument],
  );

  const onDeleteFolder = useCallback(
    (item: StorageItem) => {
      setConfirmationModal({
        title: `Delete folder — ${item.text}`,
        message: `Are you sure you want to delete this folder and everything inside it?`,
        callback: () => deleteFolder(item),
      });
      setShowConfirmationMessage(true);
    },
    [deleteFolder],
  );

  const onDeleteContentFolder = useCallback(
    (item: StorageItem) => {
      setConfirmationModal({
        title: `Delete content of — ${item.text}`,
        message: `Are you sure you want to delete everything inside this folder? The folder itself will be kept.`,
        callback: () => deleteContentFolder(item),
      });
      setShowConfirmationMessage(true);
    },
    [deleteContentFolder],
  );

  const onDecline = useCallback((): void => {
    setShowConfirmationMessage(false);
    setConfirmationModal(initialModalState);
  }, []);

  const onValidated = useCallback((): void => {
    setShowConfirmationMessage(false);
    setConfirmationModal(initialModalState);
    confirmationModal.callback();
  }, [confirmationModal]);

  return (
    <div ref={splitterContainerRef} style={{ height: '100%' }}>
      <Splitter style={{ height: '100%' }} panes={panes} onChange={onSplitterChange}>
        <div ref={treePaneRef} style={{ height: '100%' }}>
          {!hasLoadedTree ? (
            <TreeSkeleton />
          ) : (
            <TreeViewDocuments
              items={blobs}
              selectFile={setFileSelected}
              selectedFile={fileSelected}
              addNewFolder={addFolder}
              addNewDocument={addDocument}
              renameDocument={onRenameDocument}
              renameFolder={renameFolder}
              deleteDocument={onDeleteDocument}
              deleteFolder={onDeleteFolder}
              deleteContentFolder={onDeleteContentFolder}
              downloadDocument={downloadDocument}
              downloadFolder={downloadFolder}
              persistExpandKey={TREE_EXPAND_STORAGE_KEY}
            />
          )}
        </div>
        <div style={{ height: '100%' }}>{getDocumentsViewer()}</div>
      </Splitter>
      {showConfirmationMessage && (
        <Confirmation
          title={confirmationModal.title}
          message={confirmationModal.message}
          onAccept={onValidated}
          onDecline={onDecline}
        />
      )}
    </div>
  );
};

export default DocumentsManager;
